// MIRROR of server/src/modules/projectSurveys/surveyTemplate.ts for the engineer PWA, which cannot import server code.
// Bound by src/lib/surveyTemplate.parity.test.ts: edit the server file, then copy it here unchanged below this header.

// Session PJ2: THE project site survey template, version 1 (Director decisions of 27 October 2026).
//
// ONE DEFINITION. The phone sheet, the office panel, the derived figures and the supplier data sheet all read it.
// The engineer PWA mirrors it in src/lib/surveyTemplate.ts, bound by src/lib/surveyTemplate.parity.test.ts.
//
// The form is VERSIONED. Every stored survey records the version it was filled on (ProjectSurvey.formVersion) and is
// rendered by its own version. A change of MEANING (a field renamed to mean something else, a field dropped, a chip
// value reused) is a NEW version beside this one, never an edit of version 1: SURVEY_TEMPLATES keeps every version.
// Adding a hint or fixing a typo in a label is not a change of meaning.
//
// Sections are in the order an engineer walks a site. A section's scope says whether it is filled ONCE per project
// (site) or once per lift (lift). Each section and field says which survey types it applies to; the phone shows only
// those, so a hydraulic modernisation is never asked about ropes.
//
// Nothing here is a price. No figure here is invented: the template only names what is measured.

export const SURVEY_FORM_VERSION = "1";

export const SURVEY_TYPES = {
  modernisation_hydraulic: "Modernisation, hydraulic",
  modernisation_traction: "Modernisation, traction",
  full_replacement: "Full replacement",
  new_in_empty_shaft: "New lift in an existing empty shaft",
  new_build: "New build, from drawings",
} as const;
export type SurveyType = keyof typeof SURVEY_TYPES;
export const SURVEY_TYPE_CODES = Object.keys(SURVEY_TYPES) as SurveyType[];

/** Short codes used in the field list: MH, MT, FR, NE, NB. */
export const MH: SurveyType = "modernisation_hydraulic";
export const MT: SurveyType = "modernisation_traction";
export const FR: SurveyType = "full_replacement";
export const NE: SurveyType = "new_in_empty_shaft";
export const NB: SurveyType = "new_build";
export const ALL: readonly SurveyType[] = [MH, MT, FR, NE, NB];

export type SurveyScope = "site" | "lift";

export interface ChipOption {
  value: string;
  label: string;
}

/**
 * mm            a whole number of millimetres (numeric keypad, "mm" beside the box)
 * number        a plain number (unit optional: kg, V, A, kW, m/s, Hz ...)
 * chips_single  one of `options`
 * chips_multi   any of `options`
 * yes_no        yes or no
 * yes_no_unknown yes, no or unknown (stored "yes" | "no" | "unknown")
 * text          a short line of text (dictation works)
 * datetime      a date and time (stored ISO)
 * photo         a photo slot (the value lives in ProjectSurveyPhoto, keyed by the field key as the slot key)
 * table         one row per floor or level, with `columns`
 */
export type FieldKind =
  | "mm"
  | "number"
  | "chips_single"
  | "chips_multi"
  | "yes_no"
  | "yes_no_unknown"
  | "text"
  | "datetime"
  | "photo"
  | "table";

export type ColumnKind = "mm" | "number" | "chips_single" | "chips_multi" | "text";

export interface TableColumn {
  key: string;
  label: string;
  kind: ColumnKind;
  unit?: string;
  hint?: string;
  options?: readonly ChipOption[];
  /** The floor names column of the floors table: its values label every other table's rows. */
  isRowName?: boolean;
}

export interface SurveyField {
  key: string;
  label: string;
  kind: FieldKind;
  /** The survey types this field applies to. Omitted = the section's types. Ignored on a site section. */
  types?: readonly SurveyType[];
  unit?: string;
  hint?: string;
  options?: readonly ChipOption[];
  /** chips with an "Other" choice whose words go in a text box. */
  allowOther?: boolean;
  /** Only on a table: one row per floor ("floors") or per floor plus the pit bottom and the shaft top ("levels"). */
  rows?: "floors" | "levels";
  columns?: readonly TableColumn[];
  /** The only required fields in the whole form: the lift's number of floors. */
  required?: boolean;
  /**
   * Shown only when another field of the same survey holds one of these values (a follow-up question: the machine room's
   * size only when there is one). A hidden field keeps any value it already holds; it is simply not asked.
   */
  showWhen?: { field: string; in: readonly string[] };
  /** A photo slot taken once per floor (for example one per landing opening). Its slot keys are "<key>.<rowKey>". */
  photoPerRow?: "floors";
  /** A photo slot the supplier data sheet attaches by default. */
  defaultForSupplier?: boolean;
  /** On a new build survey, figures taken from drawings are marked "from drawings, to be confirmed on site". */
  fromDrawingsOnNewBuild?: boolean;
}

export interface SurveySection {
  key: string;
  title: string;
  scope: SurveyScope;
  /** Types this section applies to. Ignored for a site section (it applies to every survey). */
  types: readonly SurveyType[];
  hint?: string;
  fields: readonly SurveyField[];
}

/** The floors field: the one every table takes its rows from, and the one required field. */
export const FLOORS_SECTION = "floors";
export const FLOORS_COUNT_FIELD = "floors_count";
export const FLOORS_TABLE_FIELD = "floor_heights";
export const FLOOR_NAME_COLUMN = "name";

/** One photo slot is "extra": any photo that is not in a named slot. */
export const EXTRA_PHOTO_SLOT = "extra";
export const MAX_SURVEY_PHOTOS = 120;

// ---------------------------------------------------------------------------------------------------------------
// THE SECTIONS (version 1). Filled from the FIELD LIST in the PJ2 brief.
// ---------------------------------------------------------------------------------------------------------------

export const SURVEY_SECTIONS_V1: readonly SurveySection[] = [
  // 1. GENERAL AND ACCESS (once per project)
  {
    key: "general",
    title: "General and access",
    scope: "site",
    types: ALL,
    fields: [
      { key: "on_site_at", label: "Date and time on site", kind: "datetime" },
      { key: "building_occupied", label: "Building occupied", kind: "yes_no" },
      { key: "access_arrangements", label: "Access arrangements and keys", kind: "text", hint: "Where the keys are and how to get in. The supplier sees this: no names or phone numbers." },
      { key: "building_storeys", label: "Building height in storeys", kind: "number", unit: "storeys" },
      { key: "firefighting_lift_required", label: "Firefighting lift required", kind: "yes_no_unknown" },
      { key: "evacuation_lift_required", label: "Evacuation lift required", kind: "yes_no_unknown" },
      { key: "building_front_photo", label: "Building front", kind: "photo" },
      { key: "lift_lobby_photo", label: "Lift lobby at the main floor", kind: "photo" },
    ],
  },

  // 2. FLOORS AND TRAVEL
  {
    key: "floors",
    title: "Floors and travel",
    scope: "lift",
    types: ALL,
    fields: [
      {
        key: "floors_count",
        label: "Number of floors",
        kind: "number",
        required: true,
        hint: "Count every floor the lift serves",
      },
      {
        key: "floor_heights",
        label: "Floors",
        kind: "table",
        rows: "floors",
        fromDrawingsOnNewBuild: true,
        columns: [
          { key: "name", label: "Floor name", kind: "text", isRowName: true, hint: "For example B, G, 1, 2" },
          {
            key: "ffl_to_next",
            label: "Finished floor to next finished floor",
            kind: "mm",
            unit: "mm",
          },
          {
            key: "ffl_status",
            label: "Finished floor level",
            kind: "chips_single",
            options: [
              { value: "final", label: "Final" },
              { value: "to_screed", label: "Still to be screeded" },
            ],
          },
          {
            key: "entrance_side",
            label: "Car entrance side at this floor",
            kind: "chips_single",
            options: [
              { value: "front", label: "Front" },
              { value: "rear", label: "Rear" },
              { value: "side", label: "Side" },
            ],
          },
        ],
      },
    ],
  },

  // 3. DRAWINGS (new build)
  {
    key: "drawings",
    title: "Drawings",
    scope: "lift",
    types: [NB],
    hint: "Shaft, pit and headroom figures taken from drawings are marked from drawings, to be confirmed on site.",
    fields: [
      { key: "drawings_received", label: "Drawings received", kind: "yes_no" },
      { key: "drawing_references", label: "Drawing references", kind: "text" },
    ],
  },

  // 4. SHAFT, AT EACH LEVEL
  {
    key: "shaft",
    title: "Shaft, at each level",
    scope: "lift",
    types: ALL,
    hint: "One row for each level, plus the pit bottom and the top of the shaft.",
    fields: [
      {
        key: "shaft_levels",
        label: "Shaft at each level",
        kind: "table",
        rows: "levels",
        fromDrawingsOnNewBuild: true,
        columns: [
          { key: "clear_width", label: "Clear width", kind: "mm", unit: "mm" },
          { key: "clear_depth", label: "Clear depth", kind: "mm", unit: "mm" },
          { key: "out_of_plumb", label: "Out of plumb", kind: "mm", unit: "mm" },
          {
            key: "plumb_direction",
            label: "Out of plumb direction",
            kind: "chips_single",
            options: [
              { value: "front", label: "Front" },
              { value: "rear", label: "Rear" },
              { value: "left", label: "Left" },
              { value: "right", label: "Right" },
            ],
          },
          {
            key: "obstructions",
            label: "Obstructions",
            kind: "chips_multi",
            options: [
              { value: "pipes", label: "Pipes" },
              { value: "cables", label: "Cables" },
              { value: "beams", label: "Beams" },
              { value: "nibs", label: "Nibs" },
              { value: "other", label: "Other" },
            ],
          },
          { key: "obstruction_note", label: "Obstruction note", kind: "text" },
        ],
      },
      { key: "shaft_up_photo", label: "Shaft looking up from the pit", kind: "photo" },
      { key: "shaft_down_photo", label: "Shaft looking down from the top", kind: "photo" },
    ],
  },

  // 5. PIT AND HEADROOM
  {
    key: "pit_headroom",
    title: "Pit and headroom",
    scope: "lift",
    types: ALL,
    fields: [
      {
        key: "pit_depth",
        label: "Pit depth below the lowest finished floor",
        kind: "mm",
        unit: "mm",
        fromDrawingsOnNewBuild: true,
      },
      {
        key: "pit_floor_condition",
        label: "Pit floor condition",
        kind: "chips_single",
        options: [
          { value: "dry_sound", label: "Dry and sound" },
          { value: "damp", label: "Damp" },
          { value: "cracked", label: "Cracked or broken" },
          { value: "rough", label: "Rough, needs levelling" },
          { value: "not_built", label: "Not built yet" },
        ],
      },
      { key: "pit_water", label: "Water present in the pit", kind: "yes_no" },
      { key: "pit_drainage", label: "Pit drainage", kind: "yes_no" },
      { key: "pit_ladder", label: "Pit ladder", kind: "yes_no" },
      {
        key: "headroom",
        label: "Headroom, top finished floor to underside of the shaft ceiling",
        kind: "mm",
        unit: "mm",
        fromDrawingsOnNewBuild: true,
      },
      { key: "lifting_beam_fitted", label: "Lifting beam or hook fitted", kind: "yes_no" },
      { key: "lifting_beam_swl_marked", label: "Safe working load marked on it", kind: "yes_no", showWhen: { field: "lifting_beam_fitted", in: ["yes"] } },
      {
        key: "lifting_beam_swl",
        showWhen: { field: "lifting_beam_swl_marked", in: ["yes"] },
        label: "Marked safe working load",
        kind: "number",
        unit: "kg",
        hint: "Only if it is marked",
      },
      { key: "pit_photo", label: "Pit", kind: "photo", defaultForSupplier: true },
      { key: "headroom_photo", label: "Headroom", kind: "photo", defaultForSupplier: true },
      { key: "lifting_beam_photo", label: "Lifting beam", kind: "photo" },
    ],
  },

  // 6. WALLS AND FIXINGS
  {
    key: "walls",
    title: "Walls and fixings",
    scope: "lift",
    types: [FR, NE, NB],
    fields: [
      {
        key: "wall_front",
        label: "Front wall construction",
        kind: "chips_single",
        options: [
          { value: "concrete", label: "Concrete" },
          { value: "solid_block", label: "Solid block" },
          { value: "hollow_block", label: "Hollow block" },
          { value: "brick", label: "Brick" },
          { value: "steel_frame", label: "Steel frame" },
          { value: "timber", label: "Timber" },
          { value: "glazed", label: "Glazed" },
        ],
      },
      {
        key: "wall_rear",
        label: "Rear wall construction",
        kind: "chips_single",
        options: [
          { value: "concrete", label: "Concrete" },
          { value: "solid_block", label: "Solid block" },
          { value: "hollow_block", label: "Hollow block" },
          { value: "brick", label: "Brick" },
          { value: "steel_frame", label: "Steel frame" },
          { value: "timber", label: "Timber" },
          { value: "glazed", label: "Glazed" },
        ],
      },
      {
        key: "wall_left",
        label: "Left wall construction",
        kind: "chips_single",
        options: [
          { value: "concrete", label: "Concrete" },
          { value: "solid_block", label: "Solid block" },
          { value: "hollow_block", label: "Hollow block" },
          { value: "brick", label: "Brick" },
          { value: "steel_frame", label: "Steel frame" },
          { value: "timber", label: "Timber" },
          { value: "glazed", label: "Glazed" },
        ],
      },
      {
        key: "wall_right",
        label: "Right wall construction",
        kind: "chips_single",
        options: [
          { value: "concrete", label: "Concrete" },
          { value: "solid_block", label: "Solid block" },
          { value: "hollow_block", label: "Hollow block" },
          { value: "brick", label: "Brick" },
          { value: "steel_frame", label: "Steel frame" },
          { value: "timber", label: "Timber" },
          { value: "glazed", label: "Glazed" },
        ],
      },
      { key: "ring_beams", label: "Ring beams or floor beams at each floor", kind: "yes_no_unknown" },
      { key: "wall_front_photo", label: "Front wall", kind: "photo" },
      { key: "wall_rear_photo", label: "Rear wall", kind: "photo" },
      { key: "wall_left_photo", label: "Left wall", kind: "photo" },
      { key: "wall_right_photo", label: "Right wall", kind: "photo" },
    ],
  },

  // 7. GUIDE RAILS
  {
    key: "guide_rails",
    title: "Guide rails",
    scope: "lift",
    types: [MT, FR, NE],
    fields: [
      {
        key: "existing_rails",
        label: "Existing rails",
        kind: "chips_single",
        options: [
          { value: "none", label: "None" },
          { value: "present", label: "Present" },
        ],
      },
      { key: "car_rail_distance", showWhen: { field: "existing_rails", in: ["present"] }, label: "Car rails, distance between guides", kind: "mm", unit: "mm" },
      {
        key: "car_rail_profile",
        showWhen: { field: "existing_rails", in: ["present"] },
        label: "Car rail profile",
        kind: "chips_single",
        allowOther: true,
        options: [
          { value: "t70", label: "T70" },
          { value: "t75", label: "T75" },
          { value: "t89", label: "T89" },
          { value: "t90", label: "T90" },
          { value: "t127", label: "T127" },
          { value: "other", label: "Other" },
        ],
      },
      { key: "car_bracket_spacing", showWhen: { field: "existing_rails", in: ["present"] }, label: "Car rail bracket spacing", kind: "mm", unit: "mm" },
      {
        key: "counterweight_position",
        showWhen: { field: "existing_rails", in: ["present"] },
        label: "Counterweight position",
        kind: "chips_single",
        options: [
          { value: "side", label: "Side" },
          { value: "rear", label: "Rear" },
        ],
      },
      {
        key: "counterweight_rail_distance",
        showWhen: { field: "existing_rails", in: ["present"] },
        label: "Counterweight rails, distance between guides",
        kind: "mm",
        unit: "mm",
      },
      {
        key: "counterweight_rail_profile",
        showWhen: { field: "existing_rails", in: ["present"] },
        label: "Counterweight rail profile",
        kind: "chips_single",
        allowOther: true,
        options: [
          { value: "t70", label: "T70" },
          { value: "t75", label: "T75" },
          { value: "t89", label: "T89" },
          { value: "t90", label: "T90" },
          { value: "t127", label: "T127" },
          { value: "other", label: "Other" },
        ],
      },
      {
        key: "car_rail_layout",
        showWhen: { field: "existing_rails", in: ["present"] },
        label: "Car rail layout",
        kind: "chips_single",
        options: [
          { value: "side", label: "Side" },
          { value: "rear", label: "Rear" },
          { value: "cantilever", label: "Cantilever" },
        ],
      },
      { key: "rail_bracket_photo", label: "Rail and bracket", kind: "photo" },
      { key: "counterweight_photo", label: "Counterweight", kind: "photo" },
    ],
  },

  // 8. LANDING OPENINGS
  {
    key: "landings",
    title: "Landing openings",
    scope: "lift",
    types: ALL,
    fields: [
      {
        key: "landing_openings",
        label: "Landing openings",
        kind: "table",
        rows: "floors",
        fromDrawingsOnNewBuild: true,
        columns: [
          { key: "opening_width", label: "Structural opening width", kind: "mm", unit: "mm" },
          { key: "opening_height", label: "Structural opening height", kind: "mm", unit: "mm" },
          {
            key: "position_from_wall",
            label: "Position from a fixed wall",
            kind: "mm",
            unit: "mm",
            hint: "Measure from the same wall at every floor",
          },
          { key: "wall_thickness", label: "Front wall thickness", kind: "mm", unit: "mm" },
          {
            key: "sill",
            label: "Sill or threshold",
            kind: "chips_single",
            options: [
              { value: "none", label: "None" },
              { value: "concrete", label: "Concrete" },
              { value: "steel", label: "Steel" },
              { value: "aluminium", label: "Aluminium" },
              { value: "other", label: "Other" },
            ],
          },
          {
            key: "fire_rating",
            label: "Fire rating required",
            kind: "chips_single",
            options: [
              { value: "none", label: "None" },
              { value: "30", label: "30 minutes" },
              { value: "60", label: "60 minutes" },
              { value: "120", label: "120 minutes" },
              { value: "unknown", label: "Unknown" },
            ],
          },
        ],
      },
      {
        key: "landing_opening_photo",
        label: "Landing opening",
        kind: "photo",
        photoPerRow: "floors",
        defaultForSupplier: true,
      },
    ],
  },

  // 9. DOORS
  {
    key: "doors",
    title: "Doors",
    scope: "lift",
    types: [MH, MT, FR],
    fields: [
      {
        key: "car_door_type",
        label: "Car door type",
        kind: "chips_single",
        options: [
          { value: "manual_gate", label: "Manual gate" },
          { value: "collapsible_gate", label: "Collapsible gate" },
          { value: "auto_centre", label: "Automatic centre opening" },
          { value: "auto_side", label: "Automatic side opening" },
          { value: "none", label: "None" },
        ],
      },
      {
        key: "landing_door_type",
        label: "Landing door type",
        kind: "chips_single",
        options: [
          { value: "manual_gate", label: "Manual gate" },
          { value: "collapsible_gate", label: "Collapsible gate" },
          { value: "auto_centre", label: "Automatic centre opening" },
          { value: "auto_side", label: "Automatic side opening" },
          { value: "none", label: "None" },
        ],
      },
      {
        key: "door_operator_make_model",
        label: "Door operator make and model",
        kind: "text",
        showWhen: { field: "car_door_type", in: ["auto_centre", "auto_side"] },
      },
      {
        key: "door_lock_type",
        label: "Door lock type",
        kind: "chips_single",
        allowOther: true,
        options: [
          { value: "mechanical", label: "Mechanical" },
          { value: "electromechanical", label: "Electromechanical" },
          { value: "bolt_and_contact", label: "Bolt and contact" },
          { value: "other", label: "Other" },
        ],
      },
      {
        key: "door_lock_condition",
        label: "Door lock condition",
        kind: "chips_single",
        options: [
          { value: "good", label: "Good" },
          { value: "worn", label: "Worn" },
          { value: "replace", label: "Replace" },
        ],
      },
      { key: "car_door_photo", label: "Car door", kind: "photo" },
      { key: "landing_door_photo", label: "Landing door", kind: "photo" },
      { key: "door_operator_photo", label: "Door operator", kind: "photo" },
      { key: "door_operator_bedplate_photo", label: "Operator bedplate", kind: "photo" },
      { key: "door_lock_photo", label: "A door lock", kind: "photo" },
    ],
  },

  // 10. CAR
  {
    key: "car",
    title: "Car",
    scope: "lift",
    types: ALL,
    fields: [
      {
        key: "car_width",
        label: "Existing internal car width",
        kind: "mm",
        unit: "mm",
        types: [MH, MT, FR],
      },
      {
        key: "car_depth",
        label: "Existing internal car depth",
        kind: "mm",
        unit: "mm",
        types: [MH, MT, FR],
      },
      {
        key: "car_height",
        label: "Existing internal car height",
        kind: "mm",
        unit: "mm",
        types: [MH, MT, FR],
      },
      { key: "rated_load", label: "Required rated load", kind: "number", unit: "kg" },
      { key: "rated_persons", label: "Required rated load in persons", kind: "number", unit: "persons" },
      {
        key: "accessibility",
        label: "Accessibility needed (EN 81-70)",
        kind: "yes_no_unknown",
        hint: "A wheelchair needs at least 1100 by 1400 mm",
      },
      {
        key: "car_use",
        label: "Use of the lift",
        kind: "chips_single",
        options: [
          { value: "passenger", label: "Passenger" },
          { value: "goods", label: "Goods" },
        ],
      },
    ],
  },

  // 11. LIFT AND CONTROLS
  {
    key: "lift_controls",
    title: "Lift and controls",
    scope: "lift",
    types: [MH, MT, FR],
    fields: [
      {
        key: "lifts_in_group",
        label: "Number of lifts in the group",
        kind: "chips_single",
        options: [
          { value: "simplex", label: "Simplex (one)" },
          { value: "duplex", label: "Duplex (two)" },
          { value: "triplex_plus", label: "Triplex or more" },
        ],
      },
      {
        key: "drive_kind",
        label: "Traction or hydraulic",
        types: [FR],
        kind: "chips_single",
        options: [
          { value: "traction", label: "Traction" },
          { value: "hydraulic", label: "Hydraulic" },
        ],
      },
      {
        key: "control_system",
        label: "Control system",
        kind: "chips_single",
        allowOther: true,
        options: [
          { value: "automatic_push_button", label: "Automatic push button" },
          { value: "down_collective", label: "Down collective" },
          { value: "full_collective", label: "Full collective" },
          { value: "other", label: "Other" },
        ],
      },
      {
        key: "speed_type",
        label: "Speed type",
        kind: "chips_single",
        options: [
          { value: "single", label: "Single speed" },
          { value: "two_speed", label: "Two speed" },
          { value: "variable", label: "Variable" },
        ],
      },
      { key: "speed", label: "Speed", kind: "number", unit: "m/s" },
      { key: "controller_make_model", label: "Existing controller make and model", kind: "text" },
      { key: "drive_make_model", label: "Existing drive make and model", kind: "text" },
      {
        key: "loop_type",
        label: "Open or closed loop",
        kind: "chips_single",
        types: [MT, FR],
        options: [
          { value: "open", label: "Open loop" },
          { value: "closed", label: "Closed loop" },
        ],
      },
      {
        key: "gear_type",
        label: "Geared or gearless",
        kind: "chips_single",
        types: [MT, FR],
        options: [
          { value: "geared", label: "Geared" },
          { value: "gearless", label: "Gearless" },
        ],
      },
      { key: "lift_make", label: "Lift make", kind: "text" },
      { key: "lift_year", label: "Lift year", kind: "number", hint: "Leave empty if not known" },
      { key: "lift_serial", label: "Lift serial number", kind: "text", hint: "Leave empty if not known" },
      { key: "controller_outside_photo", label: "Controller outside", kind: "photo", defaultForSupplier: true },
      { key: "controller_inside_photo", label: "Controller inside", kind: "photo", defaultForSupplier: true },
      { key: "controller_label_photo", label: "Controller label", kind: "photo", defaultForSupplier: true },
      { key: "drive_label_photo", label: "Drive label", kind: "photo", defaultForSupplier: true },
    ],
  },

  // 12. POWER SUPPLY AND MOTOR
  {
    key: "power_motor",
    title: "Power supply and motor",
    scope: "lift",
    types: [MH, MT, FR],
    fields: [
      {
        key: "supply_volts",
        label: "Supply voltage",
        kind: "chips_single",
        allowOther: true,
        unit: "V",
        options: [
          { value: "230", label: "230 V" },
          { value: "400", label: "400 V" },
          { value: "415", label: "415 V" },
          { value: "other", label: "Other" },
        ],
      },
      {
        key: "supply_phase",
        label: "Phase",
        kind: "chips_single",
        options: [
          { value: "1", label: "Single phase" },
          { value: "3", label: "Three phase" },
        ],
      },
      { key: "supply_hz", label: "Frequency", kind: "number", unit: "Hz" },
      { key: "supply_wires", label: "Number of wires", kind: "number" },
      { key: "motor_type", label: "Motor type", kind: "text" },
      { key: "motor_current", label: "Full load current", kind: "number", unit: "A" },
      { key: "motor_kw", label: "Motor power", kind: "number", unit: "kW" },
      { key: "motor_hp", label: "Motor power", kind: "number", unit: "HP" },
      { key: "motor_rpm", label: "Motor speed", kind: "number", unit: "rpm" },
      { key: "isolator_location", label: "Isolator location", kind: "text" },
      { key: "motor_plate_photo", label: "Motor plate", kind: "photo", defaultForSupplier: true },
      { key: "isolator_photo", label: "Isolator", kind: "photo" },
    ],
  },

  // 13. BRAKE AND DRIVE (traction only)
  {
    key: "brake_drive",
    title: "Brake and drive",
    scope: "lift",
    types: [MT, FR],
    hint: "Traction lifts only. Skip for a hydraulic lift.",
    fields: [
      { key: "brake_voltage", label: "Brake voltage", kind: "number", unit: "V" },
      { key: "brake_current", label: "Brake current", kind: "number", unit: "A" },
      { key: "brake_hold_voltage", label: "Brake hold voltage", kind: "number", unit: "V" },
      { key: "ramp_voltage", label: "Retiring ramp voltage", kind: "number", unit: "V" },
      {
        key: "ramp_voltage_type",
        label: "Retiring ramp voltage type",
        kind: "chips_single",
        options: [
          { value: "ac", label: "AC" },
          { value: "dc", label: "DC" },
        ],
      },
      { key: "ramp_current", label: "Retiring ramp current", kind: "number", unit: "A" },
      { key: "buffer_stages", label: "Buffer stages", kind: "number" },
      { key: "brake_plate_photo", label: "Brake plate", kind: "photo", defaultForSupplier: true },
    ],
  },

  // 14. HYDRAULIC UNIT
  {
    key: "hydraulic",
    title: "Hydraulic unit",
    scope: "lift",
    types: [MH],
    fields: [
      {
        key: "ram_type",
        label: "Ram type",
        kind: "chips_single",
        options: [
          { value: "direct", label: "Direct" },
          { value: "indirect", label: "Indirect" },
        ],
      },
      {
        key: "ram_condition",
        label: "Ram condition",
        kind: "chips_single",
        options: [
          { value: "good", label: "Good" },
          { value: "scored", label: "Scored or marked" },
          { value: "leaking", label: "Leaking" },
          { value: "replace", label: "Replace" },
        ],
      },
      { key: "tank_make", label: "Tank make", kind: "text" },
      { key: "pump_make", label: "Pump make", kind: "text" },
      { key: "valve_make_model", label: "Valve make and model", kind: "text" },
      { key: "valve_coil_voltage", label: "Valve coil voltage", kind: "number", unit: "V" },
      { key: "tank_length", label: "Tank length", kind: "mm", unit: "mm" },
      { key: "tank_width", label: "Tank width", kind: "mm", unit: "mm" },
      { key: "tank_height", label: "Tank height", kind: "mm", unit: "mm" },
      { key: "motor_room_width", label: "Motor room width", kind: "mm", unit: "mm" },
      { key: "motor_room_depth", label: "Motor room depth", kind: "mm", unit: "mm" },
      {
        key: "oil_condition",
        label: "Oil condition",
        kind: "chips_single",
        options: [
          { value: "clean", label: "Clean" },
          { value: "dark", label: "Dark" },
          { value: "contaminated", label: "Dirty or contaminated" },
          { value: "low", label: "Low level" },
        ],
      },
      { key: "power_unit_photo", label: "Power unit", kind: "photo" },
      { key: "valve_plate_photo", label: "Valve plate", kind: "photo", defaultForSupplier: true },
      { key: "pump_motor_plate_photo", label: "Pump or motor plate", kind: "photo", defaultForSupplier: true },
      { key: "ram_head_photo", label: "Ram head", kind: "photo" },
    ],
  },

  // 15. TRACTION MACHINE, ROPES AND SAFETY GEAR
  {
    key: "traction",
    title: "Traction machine, ropes and safety gear",
    scope: "lift",
    types: [MT],
    fields: [
      { key: "machine_make_model", label: "Machine make and model", kind: "text" },
      { key: "sheave_diameter", label: "Sheave diameter", kind: "mm", unit: "mm" },
      { key: "rope_count", label: "Number of ropes", kind: "number" },
      { key: "rope_diameter", label: "Rope diameter", kind: "number", unit: "mm", hint: "For example 8 or 6.5" },
      {
        key: "rope_core",
        label: "Rope core",
        kind: "chips_single",
        options: [
          { value: "fibre", label: "Fibre" },
          { value: "steel", label: "Steel" },
        ],
      },
      {
        key: "rope_terminations",
        label: "Rope terminations",
        kind: "chips_single",
        allowOther: true,
        options: [
          { value: "wedge_socket", label: "Wedge socket" },
          { value: "resin_socket", label: "Resin socket" },
          { value: "swaged", label: "Swaged" },
          { value: "clamped", label: "Clamped" },
          { value: "other", label: "Other" },
        ],
      },
      {
        key: "roping_ratio",
        label: "Roping ratio",
        kind: "chips_single",
        options: [
          { value: "1_1", label: "1:1" },
          { value: "2_1", label: "2:1" },
        ],
      },
      { key: "governor_make", label: "Governor make", kind: "text" },
      {
        key: "safety_gear_type",
        label: "Safety gear type",
        kind: "chips_single",
        allowOther: true,
        options: [
          { value: "instantaneous", label: "Instantaneous" },
          { value: "progressive", label: "Progressive" },
          { value: "other", label: "Other" },
        ],
      },
      { key: "machine_plate_photo", label: "Machine plate", kind: "photo", defaultForSupplier: true },
      { key: "sheave_photo", label: "Sheave", kind: "photo" },
      { key: "governor_photo", label: "Governor", kind: "photo" },
      { key: "rope_terminations_photo", label: "Rope terminations", kind: "photo" },
    ],
  },

  // 16. WIRING AND SHAFT EQUIPMENT
  {
    key: "wiring",
    title: "Wiring and shaft equipment",
    scope: "lift",
    types: [MH, MT, FR],
    fields: [
      {
        key: "loom_condition",
        label: "Shaft loom condition",
        kind: "chips_single",
        options: [
          { value: "good", label: "Good" },
          { value: "poor", label: "Poor" },
          { value: "replace", label: "Replace" },
        ],
      },
      {
        key: "flex_condition",
        label: "Trailing flex condition",
        kind: "chips_single",
        options: [
          { value: "good", label: "Good" },
          { value: "poor", label: "Poor" },
          { value: "replace", label: "Replace" },
        ],
      },
      { key: "car_top_station", label: "Car top control station present", kind: "yes_no" },
      { key: "terminal_limit_switches", label: "Terminal limit switches present", kind: "yes_no_unknown" },
      { key: "encoder_present", label: "Encoder present", kind: "yes_no_unknown", types: [MT, FR] },
      { key: "proximity_switches", label: "Proximity switches present", kind: "yes_no_unknown" },
      { key: "car_top_photo", label: "Car top", kind: "photo" },
      { key: "shaft_wiring_photo", label: "Shaft wiring", kind: "photo" },
      { key: "half_way_box_photo", label: "Half way box", kind: "photo" },
    ],
  },

  // 17. CAR AND LANDING FIXTURES
  {
    key: "fixtures",
    title: "Car and landing fixtures",
    scope: "lift",
    types: ALL,
    fields: [
      {
        key: "panels_supply",
        label: "Panels",
        kind: "chips_single",
        options: [
          { value: "new_panels", label: "New car and landing panels supplied" },
          { value: "reuse_faceplates", label: "Existing faceplates reused" },
        ],
      },
      {
        key: "button_size",
        label: "Button size",
        kind: "chips_single",
        allowOther: true,
        options: [
          { value: "35", label: "35 mm" },
          { value: "70", label: "70 mm" },
          { value: "100", label: "100 mm" },
          { value: "other", label: "Other" },
        ],
      },
      {
        key: "buttons_per_landing",
        label: "Buttons per landing",
        kind: "chips_single",
        options: [
          { value: "1", label: "1" },
          { value: "2", label: "2" },
        ],
      },
      {
        key: "car_buttons_per_floor",
        label: "Car buttons per floor served",
        kind: "number",
        hint: "Usually 1",
      },
      {
        key: "fixtures_wanted",
        label: "Fittings wanted",
        kind: "chips_multi",
        options: [
          { value: "key_switches", label: "Key switches" },
          { value: "alarm_button", label: "Alarm button" },
          { value: "landing_buzzers", label: "Landing buzzers" },
          { value: "voice_unit", label: "Voice unit and speaker" },
          { value: "car_indicator", label: "Car indicator" },
          { value: "landing_indicators", label: "Landing indicators" },
          { value: "gongs", label: "Gongs" },
          { value: "hall_lanterns", label: "Hall lanterns" },
        ],
      },
      { key: "car_panel_photo", label: "Car operating panel", kind: "photo" },
      { key: "landing_panel_photo", label: "A landing panel", kind: "photo" },
    ],
  },

  // 18. OPTIONS AND STANDARDS
  {
    key: "options",
    title: "Options and standards",
    scope: "lift",
    types: ALL,
    fields: [
      {
        key: "options_wanted",
        label: "Options wanted",
        kind: "chips_multi",
        options: [
          { value: "automatic_rescue", label: "Automatic rescue device" },
          { value: "firefighting", label: "Firefighting (EN 81-72)" },
          { value: "firemans_control", label: "Fireman's control (EN 81-73)" },
          { value: "bms_link", label: "BMS link" },
          { value: "remote_monitoring", label: "Remote monitoring" },
          { value: "security_call", label: "Security call interface" },
          { value: "blue_code", label: "Blue code or priority service" },
          { value: "selective_opening", label: "Selective opening" },
        ],
      },
      {
        key: "standards_wanted",
        label: "Standards wanted",
        kind: "chips_multi",
        options: [
          { value: "en81_20_50", label: "EN 81-20 and 50" },
          { value: "en81_21", label: "EN 81-21" },
          { value: "en81_70", label: "EN 81-70" },
          { value: "en81_71", label: "EN 81-71" },
          { value: "a3_unintended_movement", label: "A3 unintended movement" },
          { value: "unknown", label: "Unknown" },
        ],
      },
    ],
  },

  // 19. MACHINE ROOM OR CONTROLLER POSITION
  {
    key: "machine_room",
    title: "Machine room or controller position",
    scope: "lift",
    types: ALL,
    fields: [
      { key: "machine_room_present", label: "Machine room present", kind: "yes_no" },
      {
        key: "mr_width",
        showWhen: { field: "machine_room_present", in: ["yes"] },
        label: "Machine room width",
        kind: "mm",
        unit: "mm",
      },
      { key: "mr_depth", showWhen: { field: "machine_room_present", in: ["yes"] }, label: "Machine room depth", kind: "mm", unit: "mm" },
      { key: "mr_height", showWhen: { field: "machine_room_present", in: ["yes"] }, label: "Machine room height", kind: "mm", unit: "mm" },
      { key: "mr_access_route", showWhen: { field: "machine_room_present", in: ["yes"] }, label: "Machine room access route", kind: "text" },
      { key: "mr_trap_door", showWhen: { field: "machine_room_present", in: ["yes"] }, label: "Trap door", kind: "yes_no" },
      { key: "mr_lifting_beam", showWhen: { field: "machine_room_present", in: ["yes"] }, label: "Lifting beam in the machine room", kind: "yes_no" },
      {
        key: "mrl_controller_position",
        showWhen: { field: "machine_room_present", in: ["no"] },
        label: "Where the controller can go at the top landing",
        kind: "text",
      },
      { key: "mrl_space_width", showWhen: { field: "machine_room_present", in: ["no"] }, label: "Space for the controller, width", kind: "mm", unit: "mm" },
      { key: "mrl_space_depth", showWhen: { field: "machine_room_present", in: ["no"] }, label: "Space for the controller, depth", kind: "mm", unit: "mm" },
      { key: "mrl_space_height", showWhen: { field: "machine_room_present", in: ["no"] }, label: "Space for the controller, height", kind: "mm", unit: "mm" },
      { key: "shaft_lighting", label: "Shaft lighting present", kind: "yes_no_unknown" },
      { key: "sockets_present", label: "Sockets present", kind: "yes_no_unknown" },
      { key: "ventilation_present", label: "Ventilation present", kind: "yes_no_unknown" },
      { key: "smoke_vent_present", label: "Smoke vent present", kind: "yes_no_unknown" },
      { key: "machine_room_photo", label: "Machine room or top landing position", kind: "photo" },
    ],
  },

  // 20. SITE LOGISTICS (once per project)
  {
    key: "logistics",
    title: "Site logistics",
    scope: "site",
    types: ALL,
    fields: [
      { key: "delivery_route", label: "Delivery route", kind: "text" },
      {
        key: "narrowest_point",
        label: "Narrowest point on the delivery route",
        kind: "mm",
        unit: "mm",
      },
      { key: "material_storage", label: "Storage for materials on site", kind: "yes_no" },
      { key: "parking", label: "Parking", kind: "text" },
      { key: "scaffold_needed", label: "Scaffold needed", kind: "yes_no_unknown" },
      { key: "working_hours", label: "Working hours and restrictions", kind: "text" },
    ],
  },
];

export const SURVEY_TEMPLATES: Readonly<Record<string, readonly SurveySection[]>> = {
  "1": SURVEY_SECTIONS_V1,
};

export function templateFor(formVersion: string): readonly SurveySection[] {
  return SURVEY_TEMPLATES[formVersion] ?? SURVEY_SECTIONS_V1;
}

export function sectionApplies(section: SurveySection, scope: SurveyScope, type: SurveyType | null): boolean {
  if (section.scope !== scope) return false;
  if (scope === "site") return true;
  // A lift with no type chosen yet is shown only the floors section (the type is asked first).
  if (type == null) return section.key === FLOORS_SECTION;
  return section.types.includes(type);
}

export function fieldApplies(section: SurveySection, field: SurveyField, type: SurveyType | null): boolean {
  if (section.scope === "site") return true;
  if (type == null) return section.key === FLOORS_SECTION;
  return (field.types ?? section.types).includes(type);
}

/** The sections a survey of this scope and type shows, in walking order. */
export function sectionsFor(formVersion: string, scope: SurveyScope, type: SurveyType | null): SurveySection[] {
  return templateFor(formVersion).filter((s) => sectionApplies(s, scope, type));
}

/** The fields of a section a survey of this type shows. */
export function fieldsFor(section: SurveySection, type: SurveyType | null): SurveyField[] {
  return section.fields.filter((f) => fieldApplies(section, f, type));
}

export function findField(formVersion: string, key: string): { section: SurveySection; field: SurveyField } | null {
  for (const section of templateFor(formVersion)) {
    const field = section.fields.find((f) => f.key === key);
    if (field) return { section, field };
  }
  return null;
}

export function findSection(formVersion: string, key: string): SurveySection | null {
  return templateFor(formVersion).find((s) => s.key === key) ?? null;
}

/**
 * Whether a field is asked, given the survey's current values. A field with no showWhen is always asked; one whose gate
 * field is itself hidden is hidden too. `valueOf` returns a field's stored value (yes_no and yes_no_unknown are stored
 * as "yes" | "no" | "unknown").
 */
export function fieldShown(
  formVersion: string,
  field: SurveyField,
  valueOf: (fieldKey: string) => unknown,
  depth = 0,
): boolean {
  const gate = field.showWhen;
  if (!gate) return true;
  if (depth > 5) return false;
  const gateDef = findField(formVersion, gate.field);
  if (gateDef && !fieldShown(formVersion, gateDef.field, valueOf, depth + 1)) return false;
  const v = valueOf(gate.field);
  if (Array.isArray(v)) return v.some((x) => gate.in.includes(String(x)));
  return v != null && gate.in.includes(String(v));
}

/** Every photo slot key in a version, with its section. */
export function photoSlots(formVersion: string): { section: SurveySection; field: SurveyField }[] {
  const out: { section: SurveySection; field: SurveyField }[] = [];
  for (const section of templateFor(formVersion)) {
    for (const field of section.fields) if (field.kind === "photo") out.push({ section, field });
  }
  return out;
}

/** Every field in a version that is not a photo slot (the "fields" of acceptance test 1). */
export function dataFieldCount(formVersion: string): number {
  return templateFor(formVersion).reduce((n, s) => n + s.fields.filter((f) => f.kind !== "photo").length, 0);
}
